import { Building2, ClipboardList, FileText } from "lucide-react";
import type { ReactNode } from "react";
import { CopyButton } from "@/components/shared/copy-button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { formatDate } from "@/lib/format";

// Compact preview shown when the user hovers a policy / claim / agency
// number anywhere in a table. The trigger is whatever the caller renders
// (usually the Link to the entity dashboard); the card content is built from
// preview data the parent already has — no extra fetch on hover.

export type PolicyPreview = {
  kind: "policy";
  number: string;
  insuredName: string | null;
  status: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
};

export type ClaimPreview = {
  kind: "claim";
  number: string;
  isRestricted: boolean;
  claimantName: string | null;
  status: string | null;
  lossDate: string | null;
  lossType: string | null;
};

export type AgencyPreview = {
  kind: "agency";
  number: string;
  name: string | null;
};

export type NumberPreview = PolicyPreview | ClaimPreview | AgencyPreview;

interface NumberHoverCardProps {
  preview: NumberPreview;
  /** The trigger element — typically a <Link> rendering the number itself. */
  children: ReactNode;
  /** Friendly label for the copy toast, e.g. "policy WC-001023". */
  copyLabel?: string;
}

export function NumberHoverCard({ preview, children, copyLabel }: NumberHoverCardProps) {
  const KindIcon =
    preview.kind === "policy" ? FileText : preview.kind === "claim" ? ClipboardList : Building2;
  const kindLabel =
    preview.kind === "policy" ? "Policy" : preview.kind === "claim" ? "Claim" : "Agency";
  const resolvedCopyLabel = copyLabel ?? `${kindLabel.toLowerCase()} ${preview.number}`;

  return (
    <span className="group/numbercell inline-flex items-center gap-1.5">
      <HoverCard>
        <HoverCardTrigger asChild>{children}</HoverCardTrigger>
        <HoverCardContent align="start">
          <div className="flex items-start gap-2">
            <KindIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-primary">{preview.number}</div>
              <div className="text-muted-foreground text-xs">{kindLabel}</div>
            </div>
          </div>
          <div className="mt-2 space-y-1 text-xs">
            {preview.kind === "policy" && (
              <>
                {preview.insuredName && <Row label="Insured" value={preview.insuredName} />}
                {preview.status && <Row label="Status" value={preview.status} />}
                {(preview.effectiveDate || preview.expirationDate) && (
                  <Row
                    label="Term"
                    value={`${formatDate(preview.effectiveDate)} → ${formatDate(preview.expirationDate)}`}
                  />
                )}
              </>
            )}
            {preview.kind === "claim" && (
              <>
                {preview.isRestricted ? (
                  <Row label="Claimant" value="Restricted" />
                ) : (
                  preview.claimantName && <Row label="Claimant" value={preview.claimantName} />
                )}
                {preview.status && <Row label="Status" value={preview.status} />}
                {preview.lossType && <Row label="Loss type" value={preview.lossType} />}
                {preview.lossDate && <Row label="Loss date" value={formatDate(preview.lossDate)} />}
              </>
            )}
            {preview.kind === "agency" && preview.name && <Row label="Name" value={preview.name} />}
          </div>
        </HoverCardContent>
      </HoverCard>
      {/* Inline copy icon — invisible until the user hovers the number cell, so
          tables don't get visually noisy with one icon per row. */}
      <CopyButton
        value={preview.number}
        label={resolvedCopyLabel}
        className="opacity-0 transition-opacity group-hover/numbercell:opacity-100 focus-within:opacity-100 focus-visible:opacity-100"
      />
    </span>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
      <span className="flex-1 truncate text-foreground">{value}</span>
    </div>
  );
}
